import {
  ChangeDetectorRef,
  Component,
  ElementRef,
  HostListener,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { CurrentUserService } from 'src/app/services/current-user.service';
import { DataService } from 'src/app/services/data.service';
import { MapService } from 'src/app/services/map.service';
import { UserPreferenceService } from 'src/app/services/user-preference.service';
import { ToastService } from 'src/app/services/toast.service';
import { ContentDeleteModalComponent } from 'src/app/modal/content-delete-modal/content-delete-modal.component';

@Component({
  selector: 'app-content-creator',
  templateUrl: './content-creator.component.html',
  styleUrls: ['./content-creator.component.scss'],
  standalone: false,
})
export class ContentCreatorComponent implements OnInit, OnDestroy {
  userId: any;
  contentCreatorData: any[] = [];
  activePath: string;
  isClassAdded = false;
  mapList: any[] = [];
  userPreferences: any = {};
  deletingId: any = null;

  @ViewChild('filterWrap') filterWrap?: ElementRef<HTMLElement>;

  selectedTab: 'created' | 'purchased' | 'drafts' = 'created';
  search = '';
  selectedType = '';
  filterOpen = false;

  private isDraft(content: any): boolean {
    return content?.status === 'draft' || content?.is_draft === true;
  }

  /**
   * The content API (api/ui/content/all/) reports ownership as `access_type`
   * ('creator' | 'purchase' | 'gift') plus `wp_creator_id` — it never sends
   * `created_by`, which this page used to compare, so everything landed under
   * Purchased and your own content showed "View" instead of "Edit".
   */
  isMine(content: any): boolean {
    if (content?.access_type) return content.access_type === 'creator';
    return content?.wp_creator_id == this.userId || content?.created_by == this.userId;
  }

  get createdContent(): any[] {
    return (this.contentCreatorData || []).filter(
      (c) => this.isMine(c) && !this.isDraft(c),
    );
  }

  get purchasedContent(): any[] {
    return (this.contentCreatorData || []).filter(
      (c) => !this.isMine(c),
    );
  }

  get draftContent(): any[] {
    return (this.contentCreatorData || []).filter(
      (c) => this.isMine(c) && this.isDraft(c),
    );
  }

  get tabContent(): any[] {
    if (this.selectedTab === 'purchased') return this.purchasedContent;
    if (this.selectedTab === 'drafts') return this.draftContent;
    return this.createdContent;
  }

  get contentTypes(): string[] {
    const types = new Set<string>();
    for (const c of this.tabContent) {
      const t = (c.type || '').trim();
      if (t) types.add(t);
    }
    if (this.selectedType) types.add(this.selectedType);
    return Array.from(types).sort((a, b) => a.localeCompare(b));
  }

  get isFiltered(): boolean {
    return !!this.selectedType || !!this.search.trim();
  }

  get visibleContent(): any[] {
    let list = this.tabContent;
    if (this.selectedType) {
      list = list.filter((c) => (c.type || '') === this.selectedType);
    }
    const q = this.search.trim().toLowerCase();
    if (!q) return list;
    return list.filter((c) =>
      [c.content_title, c.type, c.gamesystem, c.map_name].some((v) =>
        (v || '').toLowerCase().includes(q),
      ),
    );
  }

  typeCount(type: string): number {
    return this.tabContent.filter((c) => (c.type || '') === type).length;
  }

  typeSlug(type: string): string {
    return (
      (type || 'other')
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-+|-+$/g, '') || 'other'
    );
  }

  selectType(type: string) {
    this.selectedType = this.selectedType === type ? '' : type;
    this.filterOpen = false;
  }

  toggleFilter(ev: Event) {
    ev.stopPropagation();
    this.filterOpen = !this.filterOpen;
  }

  clearFilters() {
    this.selectedType = '';
    this.search = '';
    this.filterOpen = false;
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(ev: MouseEvent) {
    if (!this.filterOpen) return;
    const root = this.filterWrap?.nativeElement;
    if (root && !root.contains(ev.target as Node)) {
      this.filterOpen = false;
    }
  }

  @HostListener('document:keydown.escape')
  onEscape() {
    this.filterOpen = false;
  }

  contentQueryParams(content: any) {
    return {
      mode: 'edit',
      id: content.id,
      mapcode: content.mapcode,
      citycode: content.citycode ? content.citycode : 'null',
      isEditable: this.isMine(content),
    };
  }

  constructor(
    private dataService: DataService,
    private currentUserService: CurrentUserService,
    private mapService: MapService,
    private modalService: NgbModal,
    private userPreferenceService: UserPreferenceService,
    private toastService: ToastService,
    private cdr: ChangeDetectorRef,
  ) {
    this.isClassAdded = true;
  }

  ngOnDestroy(): void {
    this.mapService.removeOldMap();
  }

  ngOnInit(): void {
    this.activePath = localStorage.getItem('activePath');
    this.currentUserService.currentUser.subscribe((data) => {
      if (data) {
        this.userId = Number(data.id);
        this.getContent(this.userId);
        this.userPreferenceService.fetchPreferences(this.userId);
        this.cdr.detectChanges();
      }
    });

    this.userPreferenceService.preferences$.subscribe((prefs) => {
      this.userPreferences = prefs || {};
      this.cdr.detectChanges();
    });
  }

  getContent(userId) {
    this.dataService.getContentDataByUserId(userId).subscribe({
      next: (data: any) => {
        this.contentCreatorData = data?.results || data?.res || [];
        this.cdr.detectChanges();
      },
      error: () => {
        this.contentCreatorData = [];
        this.cdr.detectChanges();
      },
    });
  }

  deleteContentRecord(content) {
    if (this.userPreferences['hide_content_delete_confirmation']) {
      this.deletingId = content.id;
      this.dataService.deleteContentById(content.id, this.userId).subscribe({
        next: (res) => {
          this.deletingId = null;
          if (res) {
            this.toastService.showSuccess(
              'Content deleted.',
              'success',
            );
            this.getContent(this.userId);
          }
          this.cdr.detectChanges();
        },
        error: (error) => {
          this.deletingId = null;
          console.error('Error deleting content', error);
          this.cdr.detectChanges();
        },
      });
      return;
    }

    const modalRef = this.modalService.open(ContentDeleteModalComponent, {
      size: 'md',
      backdrop: 'static',
      centered: true,
    });
    modalRef.componentInstance.pagename = 'Content';
    modalRef.componentInstance.id = content.id;
    modalRef.componentInstance.user_id = this.userId;
    modalRef.result.then(
      (result) => {
        if (result) {
          this.getContent(this.userId);
        }
        this.cdr.detectChanges();
      },
      () => {},
    );
  }
}
